import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [err, setErr] = useState("");
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back!");
            const target = location.state?.from?.pathname || "/dashboard";
            navigate(target, { replace: true });
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background relative overflow-hidden">
            <div aria-hidden className="absolute -top-40 -left-40 w-[420px] h-[420px] rounded-full bg-primary/30 blur-3xl" />
            <div aria-hidden className="absolute bottom-0 right-0 w-[480px] h-[480px] rounded-full bg-accent/20 blur-3xl" />

            <div className="relative flex items-center justify-center px-6 py-12 z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md glass rounded-3xl p-8 shadow-2xl"
                >
                    <Link to="/" className="inline-flex items-center gap-2 mb-8">
                        <div className="w-9 h-9 rounded-xl bg-white grid place-items-center shadow-[0_0_20px_rgba(255,87,51,0.15)] overflow-hidden p-1">
                            <img src="/logo.png" alt="ParshHR Logo" className="w-full h-full object-contain" />
                        </div>
                        <span className="font-heading font-bold text-xl">ParshHR</span>
                    </Link>

                    <h1 className="font-heading text-3xl font-bold tracking-tight">Welcome back</h1>
                    <p className="text-muted-foreground text-sm mt-1">Sign in to your ParshHR workspace.</p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1.5" />
                        </div>
                        <div>
                            <div className="flex justify-between items-center">
                                <Label htmlFor="password">Password</Label>
                                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">Forgot?</Link>
                            </div>
                            <div className="relative mt-1.5">
                                <Input 
                                    id="password" 
                                    type={showPassword ? "text" : "password"} 
                                    value={password} 
                                    onChange={(e) => setPassword(e.target.value)} 
                                    required 
                                    data-testid="login-password" 
                                    className="pr-10" 
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {err && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}

                        <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="login-submit">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                        </Button>

                        <div className="text-center text-sm text-muted-foreground">
                            New to ParshHR?{" "}
                            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">Create account</Link>
                        </div>
                    </form>


                </motion.div>
            </div>

            <div className="hidden lg:flex items-center justify-center relative z-10 p-12">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="max-w-lg"
                >
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-4">Modern HRMS</div>
                    <h2 className="font-heading text-5xl font-bold leading-[1.05] mb-6">
                        Run your <span className="text-gradient-primary">people ops</span> with clarity & soul.
                    </h2>
                    <p className="text-muted-foreground">
                        From hiring to relieving, ParshHR brings recruitment, attendance, leaves, payroll and culture into one beautifully crafted dashboard.
                    </p>
                    <div className="mt-8 grid grid-cols-3 gap-4">
                        {[
                            ["12k+", "active employees"],
                            ["150+", "growing teams"],
                            ["4.9★", "user rating"],
                        ].map(([k, v]) => (
                            <div key={k} className="glass rounded-2xl p-4">
                                <div className="font-heading font-bold text-2xl">{k}</div>
                                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{v}</div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
